import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, MessageSquare, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import type { ChatConversation } from "@shared/schema";

type Filter = "all" | "yes" | "no";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Mind" },
  { value: "yes", label: "Űrlappal zárult" },
  { value: "no", label: "Űrlap nélkül" },
];

/**
 * "AI beszélgetések" — az AI chat perzisztált átiratai, hogy az admin
 * visszanézhesse és elemezhesse a beszélgetéseket. Szűrhető aszerint, hogy a
 * beszélgetés kapcsolatfelvétellel (űrlappal) zárult-e.
 */
export function AdminChatLog() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin", "chat-conversations", filter],
    queryFn: () =>
      api<ChatConversation[]>(
        `/api/admin/chat-conversations${
          filter === "all" ? "" : `?converted=${filter}`
        }`,
      ),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/chat-conversations/${id}`, { method: "DELETE" }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "chat-conversations"] }),
  });

  const convertedCount = (q.data ?? []).filter((c) => c.convertedToLead).length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">AI beszélgetések</h1>
          <p className="text-sm text-brand-500 mt-1">
            Az AI chat átiratai — {q.data?.length ?? 0} beszélgetés, ebből{" "}
            {convertedCount} zárult kapcsolatfelvétellel.
          </p>
        </div>
        <div className="flex gap-1 bg-brand-100 rounded-lg p-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                filter === f.value
                  ? "bg-white text-brand-900 shadow-sm"
                  : "text-brand-500 hover:text-brand-800"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 card overflow-hidden">
        {q.isLoading && <div className="p-6 text-brand-500">Betöltés…</div>}
        {q.data && q.data.length === 0 && (
          <div className="p-6 text-brand-500">
            Nincs a szűrőnek megfelelő beszélgetés.
          </div>
        )}
        {q.data && q.data.length > 0 && (
          <ul className="divide-y divide-brand-100">
            {q.data.map((c) => {
              const expanded = openId === c.id;
              const firstUser =
                c.messages.find((m) => m.role === "user")?.content ??
                "(üres beszélgetés)";
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(expanded ? null : c.id)}
                    className="w-full p-4 text-left flex items-start gap-3 hover:bg-brand-50/50"
                  >
                    {c.convertedToLead ? (
                      <CheckCircle2
                        size={18}
                        className="text-emerald-600 mt-0.5 shrink-0"
                      />
                    ) : (
                      <MessageSquare
                        size={18}
                        className="text-brand-400 mt-0.5 shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {firstUser}
                        </span>
                        {c.convertedToLead && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                            lead
                          </span>
                        )}
                        <span className="text-xs text-brand-500 ml-auto whitespace-nowrap">
                          {new Date(c.updatedAt).toLocaleString("hu-HU")}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-brand-500">
                        {c.messageCount} üzenet
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 -mt-1">
                      <div className="ml-7 pl-4 border-l-2 border-brand-200 space-y-2">
                        {c.messages.map((m, i) => (
                          <div
                            key={i}
                            className={`text-sm ${
                              m.role === "user" ? "" : "pl-3"
                            }`}
                          >
                            <div className="flex items-center gap-1.5 mb-0.5">
                              {m.role === "assistant" && (
                                <Sparkles
                                  size={11}
                                  className="text-accent"
                                />
                              )}
                              <span className="text-[10px] uppercase tracking-wide font-semibold text-brand-400">
                                {m.role === "user" ? "Látogató" : "AI"}
                              </span>
                            </div>
                            <div className="text-brand-800 whitespace-pre-wrap">
                              {m.content}
                            </div>
                          </div>
                        ))}

                        <div className="flex flex-wrap gap-2 text-xs text-brand-500 pt-1">
                          {c.sourceUrl && (
                            <a
                              href={c.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-brand-900"
                            >
                              Forrás ↗
                            </a>
                          )}
                          {c.convertedToLead && (
                            <span className="text-emerald-700">
                              → az Üzenetek közé került lead
                            </span>
                          )}
                        </div>

                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm("Véglegesen törli a beszélgetést?"))
                                del.mutate(c.id);
                            }}
                            className="btn-danger text-xs"
                          >
                            <Trash2 size={14} />
                            Törlés
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
